/**
 * L'avvio del pannello.
 *
 * L'ordine delle importazioni qui sotto **non è alfabetico e non è casuale**: è
 * l'ordine in cui i fogli di stile devono sovrapporsi, e in CSS l'ordine è
 * semantica. I caratteri per primi, perché nessuno li sovrascrive. Poi i token,
 * che dichiarano le variabili. Poi le fondamenta, che le usano. I fogli dei
 * componenti li inserisce PrimeVue da sé, e stanno in uno strato `@layer`
 * dedicato (vedi `cssLayer` più sotto): è ciò che permette a una nostra regola
 * di correggerne una di libreria con un selettore semplice, senza inseguire la
 * specificità a colpi di `!important`.
 */

// I caratteri sono **nostri**: nessun dominio di terzi (§13, divieto 10). Non è
// solo riservatezza — è che una barra laterale disegnata con il carattere di
// sistema mentre il collegamento a un CDN va in timeout è un pannello rotto per
// tre secondi, ogni volta, per cinquecento persone.
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';

import '@bestehr/brand/tokens.css';
import './styles/base.css';
import './styles/turni.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import Tooltip from 'primevue/tooltip';

import App from './App.vue';
import { router } from './router';
import { avviaControlloVersione } from './lib/versione-nuova';
import preset from './theme/preset';
import { it } from './i18n/it';

const app = createApp(App);

app.use(createPinia());

app.use(PrimeVue, {
  theme: {
    preset,
    options: {
      /*
       * Il tema **non** scrive una classe sul documento.
       *
       * PrimeVue offre `darkModeSelector`, che aggiunge una classe quando è
       * attivo il tema scuro. Qui non serve e sarebbe dannoso: i nostri token
       * decidono già chiaro e scuro con `data-beste-theme` più la preferenza di
       * sistema, e il preset non fa che rimandare a quelle variabili. Avere due
       * interruttori per la stessa cosa significa che prima o poi si trovano in
       * disaccordo — pannello chiaro e menu a tendina scuri, o viceversa.
       *
       * `false` disattiva il meccanismo di PrimeVue senza disattivare il tema
       * scuro: quello continua a funzionare, ma da un posto solo.
       */
      darkModeSelector: false,

      /*
       * I fogli dei componenti stanno in uno strato a bassa priorità.
       *
       * Con `@layer`, qualunque nostra regola scritta fuori dagli strati vince
       * su quelle di PrimeVue **a prescindere dalla specificità**. È ciò che
       * rende possibile correggere un componente con un selettore semplice e
       * leggibile invece di inseguire `.p-datatable .p-datatable-tbody > tr > td`
       * con un `!important` in fondo.
       *
       * `order` nomina **due** strati, e il primo è nostro. È l'unico posto in
       * cui l'ordine si può dichiarare davvero: PrimeVue inserisce i propri
       * `<style>` *in testa* al documento, cioè prima di `base.css`, quindi è la
       * sua riga a fissare per prima la scala. Dichiarando lì solo `primevue`,
       * l'`@layer azzeramento, primevue` scritto in `base.css` arrivava dopo e
       * poteva soltanto **aggiungere** `azzeramento` in coda — sopra PrimeVue,
       * cioè l'esatto contrario di quello che serviva: l'azzeramento tornava a
       * cancellare spaziature, bordi e fondi dei componenti della libreria.
       *
       * Ordine finale: `azzeramento` (il nostro pavimento) < `primevue` (i
       * componenti) < tutto ciò che in `base.css` non è stratificato (le nostre
       * correzioni). La spiegazione per esteso sta in testa a `base.css`.
       */
      cssLayer: { name: 'primevue', order: 'azzeramento, primevue' },
    },
  },
  ripple: false, // l'onda al tocco è un vezzo di Material: qui il riscontro lo dà lo stato :active
  locale: it,
});

app.use(ToastService);
app.use(ConfirmationService);
app.directive('tooltip', Tooltip);

app.use(router);

/*
 * Il montaggio aspetta che il router abbia deciso.
 *
 * `app.mount()` chiamato subito disegna un primo fotogramma con la rotta
 * iniziale ancora vuota, e la guardia d'accesso — che è asincrona, perché deve
 * chiedere al server chi sta guardando — arriva dopo. Per chi apre il pannello
 * senza sessione valida quel fotogramma è un lampo di applicazione completa
 * prima del rimbalzo all'ingresso.
 *
 * `App.vue` si difende comunque per conto suo, non disegnando niente finché la
 * rotta non ha un nome: le due protezioni non si sovrappongono inutilmente,
 * risolvono lo stesso problema a due profondità diverse. Questa toglie il
 * fotogramma; quella copre il caso in cui una navigazione successiva
 * ripresentasse una rotta non risolta.
 *
 * `void` sul `catch`: se la risoluzione iniziale fallisce — una guardia che
 * lancia, una rotta di partenza che non si risolve — montare comunque è
 * l'unica strada che porta a qualcosa a schermo. Non montare lascerebbe il
 * velo d'avvio da solo, e sarebbe l'unica cosa peggiore del lampo.
 */
void router.isReady().finally(() => {
  app.mount('#app');
  togliIlVelo();
});

// Un Cruscotto uscito mentre questa scheda è aperta: vedi `lib/versione-nuova.ts`.
avviaControlloVersione();

/*
 * Il velo d'avvio se ne va **dopo** il montaggio, non prima.
 *
 * Toglierlo in anticipo scoprirebbe una pagina bianca per la frazione di
 * secondo che serve a Vue per il primo disegno — cioè si vedrebbe esattamente
 * lo sfarfallio che il velo esiste per evitare. La dissolvenza è affidata al
 * CSS, e `remove()` arriva alla fine della transizione: se le animazioni sono
 * disattivate, la transizione dura zero e l'evento arriva subito.
 */
function togliIlVelo(): void {
  const boot = document.getElementById('boot');
  if (!boot) return;

  boot.addEventListener('transitionend', () => boot.remove(), { once: true });
  boot.classList.add('boot--via');
  // Rete di sicurezza: se per qualsiasi motivo la transizione non parte (elemento
  // già invisibile, motore che salta l'animazione), il velo non deve restare lì
  // a coprire il pannello per sempre.
  setTimeout(() => boot.remove(), 1_000);
}
